<template>
  <TwoColumnFormCard>
    <h2 class="mb-4 text-xl">{{ t('dashboard.vacancy.published') }}</h2>
    <div
      v-if="dashboardStore.isLoading"
      class="flex h-93 items-center justify-center"
    >
      <LoadingSpinner class="h-18 w-18 text-indigo-400" />
    </div>
    <template v-else>
      <p class="mb-2 text-sm">
        <span class="text-gray-500"
          >{{ totalVacancies }} {{ t('dashboard.total') }},
        </span>
        <span
          :class="{
            'text-green-600': vacancyDifference >= 0,
            'text-red-600': vacancyDifference < 0,
          }"
          >{{ vacancyDifference >= 0 ? '+' : '' }}{{ vacancyDifference }}%
        </span>
        <span class="text-gray-500">{{ t('dashboard.difference') }}</span>
      </p>
      <TimeSeriesChart
        class="h-86"
        :datasets="publishedVacanciesData"
        :options="publicationChartOptions"
      />
    </template>
  </TwoColumnFormCard>

  <div class="mt-8 grid grid-cols-2 gap-8">
    <TwoColumnFormCard>
      <h2 class="mb-4 text-xl">{{ t('dashboard.vacancy.educations') }}</h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-82 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <BarChart
          :dataset="educationLevelsData"
          :options="educationChartOptions"
          :sortData="true"
          class="h-72"
        />
        <p class="mt-2 text-xs text-gray-500">
          * {{ t('dashboard.vacancy.educationsHint') }}
        </p>
      </template>
    </TwoColumnFormCard>

    <TwoColumnFormCard>
      <h2 class="mb-4 text-xl">
        {{ t('dashboard.vacancy.disciplines') }}
      </h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-82 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <BarChart
          :dataset="disciplinesData"
          :sortData="true"
          :options="disciplineChartOptions"
          class="h-72"
        />
        <p class="mt-2 text-xs text-gray-500">
          * {{ t('dashboard.vacancy.disciplinesHint') }}
        </p>
      </template>
    </TwoColumnFormCard>

    <TwoColumnFormCard>
      <h2 class="mb-4 text-xl">{{ t('dashboard.vacancy.regions') }}</h2>
      <div
        v-if="dashboardStore.isLoading"
        class="flex h-151 items-center justify-center"
      >
        <LoadingSpinner class="h-18 w-18 text-indigo-400" />
      </div>
      <template v-else>
        <MapChart :dataset="regionsData" />
      </template>
    </TwoColumnFormCard>

    <div class="grid gap-8">
      <TwoColumnFormCard>
        <h2 class="mb-6 flex items-center gap-x-2 text-xl">
          <TrophyIcon class="h-6 w-6 text-yellow-500" />
          {{ t('dashboard.vacancy.companiesRanking') }}
        </h2>
        <div
          v-if="dashboardStore.isLoading"
          class="flex h-69 items-center justify-center"
        >
          <LoadingSpinner class="h-18 w-18 text-indigo-400" />
        </div>
        <template v-else>
          <ol class="ml-2.5 flex list-decimal flex-col gap-y-1">
            <li
              v-for="([label, value], index) in sortedCompanies"
              :key="index"
              class="px-2"
            >
              <span class="text-gray-800">{{ label }}: </span>
              <span class="font-bold">{{ value }}</span>
            </li>
          </ol>
        </template>
      </TwoColumnFormCard>
      <TwoColumnFormCard>
        <h2 class="mb-6 flex items-center gap-x-2 text-xl">
          {{ t('dashboard.vacancy.averageDuration') }}
        </h2>
        <div
          v-if="dashboardStore.isLoading"
          class="flex h-69 items-center justify-center"
        >
          <LoadingSpinner class="h-18 w-18 text-indigo-400" />
        </div>
        <template v-else>
          <p class="mt-12 text-center text-4xl text-gray-700">
            {{ t('dashboard.vacancy.duration', { days: averageDurationDays }) }}
          </p>
          <p class="mt-2 text-center text-sm text-gray-500">
            *
            {{
              t('dashboard.vacancy.openEnded', {
                total: totalOpenEndedVacancies,
                percent:
                  Math.round((totalOpenEndedVacancies * 100) / totalVacancies) /
                  100,
              })
            }}
          </p>
        </template>
      </TwoColumnFormCard>
    </div>
  </div>
</template>

<script setup>
import {
  disciplineCategories,
  useDashboardStore,
} from '../../store/dashboardStore'
import { computed, ref } from 'vue'
import TwoColumnFormCard from '../TwoColumnFormCard.vue'
import { useI18n } from 'vue-i18n'
import TimeSeriesChart from './TimeSeriesChart.vue'
import { TrophyIcon } from '@heroicons/vue/24/outline'
import MapChart from './MapChart.vue'
import BarChart from './BarChart.vue'
import {
  addDays,
  addMonths,
  format,
  parseISO,
  subDays,
  subMonths,
} from 'date-fns'
import LoadingSpinner from '../LoadingSpinner.vue'

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const dashboardStore = useDashboardStore()

const publishedVacanciesData = ref([])
const regionsData = ref({})
const educationLevelsData = ref({})
const disciplinesData = ref({})
const totalVacancies = ref(0)
const vacancyDifference = ref(0)
const sortedCompanies = ref([])

const averageDurationDays = ref(0)
const totalOpenEndedVacancies = ref(0)

const publicationChartOptions = computed(() => ({
  plugins: {
    // legend: {
    //   display: true,
    // },
    // tooltip: {
    //   callbacks: {
    //     label(context) {
    //       const label = context.dataset.label
    //         ? `${context.dataset.label}: `
    //         : ''
    //       let dateSuffix = ''
    //       if (context.dataset.label === t('dashboard.vacancy.previousPeriod')) {
    //         const date = context.chart.data.labels[context.dataIndex]
    //         const previousPeriodDateStr = format(
    //           subMonths(date, 1),
    //           'EEEE, dd-MM-yyyy',
    //         )
    //         dateSuffix = ` (${previousPeriodDateStr})`
    //       }
    //       return `${label}${context.formattedValue}${dateSuffix}`
    //     },
    //   },
    // },
  },
}))

const educationChartOptions = computed(() => ({
  indexAxis: 'y',
  scales: { x: { max: totalVacancies.value } },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 4,
      clip: false,
      formatter: (value) =>
        totalVacancies.value
          ? `${Math.round(((value * 10) / totalVacancies.value) * 100) / 10}%`
          : '0%',
    },
  },
}))

const disciplineChartOptions = computed(() => ({
  indexAxis: 'y',
  scales: { x: { max: totalVacancies.value } },
  plugins: {
    datalabels: {
      anchor: 'end',
      align: 'right',
      offset: 4,
      clip: false,
      formatter: (value) =>
        totalVacancies.value
          ? `${Math.round(((value * 10) / totalVacancies.value) * 100) / 10}%`
          : '0%',
    },
    tooltip: {
      footerFont: {
        weight: 'normal',
      },
      callbacks: {
        footer(context) {
          const category = context[0].label
          const relatedDisciplines = Object.keys(disciplineCategories).filter(
            (key) => disciplineCategories[key] === category,
          )
          if (relatedDisciplines.length) {
            return `${t('dashboard.disciplines')}:\n- ${relatedDisciplines.join('\n- ')}`
          }
          return ''
        },
      },
    },
  },
}))

dashboardStore
  .fetchVacancies()
  .then(
    ({
      currentPeriod,
      previousPeriod,
      regions,
      disciplines,
      educationLevels,
      companies,
      totalCurrent,
      totalPrevious,
      publicationHoursDuration,
    }) => {
      publishedVacanciesData.value = [
        {
          label: t('dashboard.vacancy.published'), // t('dashboard.vacancy.currentPeriod'),
          data: currentPeriod,
        },
        // {
        //   label: t('dashboard.vacancy.previousPeriod'),
        //   data: Object.entries(previousPeriod).reduce((acc, [date, value]) => {
        //     const shiftedDate = format(addMonths(date, 1), 'yyyy-MM-dd')
        //     acc[shiftedDate] = value
        //     return acc
        //   }, {}),
        //   hidden: true,
        //   backgroundColor: 'oklch(64.6% 0.222 41.116)', // orange-600
        //   borderColor: 'oklch(83.7% 0.128 66.29)', // orange-300
        // },
      ]
      regionsData.value = {
        label: t('dashboard.vacancy.published'),
        data: regions,
      }
      educationLevelsData.value = {
        label: t('dashboard.vacancy.published'),
        data: educationLevels,
      }
      disciplinesData.value = {
        label: t('dashboard.vacancy.published'),
        data: disciplines,
      }

      const difference = totalCurrent / totalPrevious - 1
      vacancyDifference.value = Math.round(difference * 1000) / 10 // 1 decimal place
      totalVacancies.value = totalCurrent

      sortedCompanies.value = Object.entries(companies)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)

      const totalDurationHours = publicationHoursDuration.reduce(
        (acc, duration) => {
          acc += duration
          return acc
        },
        0,
      )
      const averageDuration =
        totalDurationHours / 24 / publicationHoursDuration.length
      averageDurationDays.value = Math.round(averageDuration * 10) / 10 // 1 decimal place

      totalOpenEndedVacancies.value =
        totalCurrent - publicationHoursDuration.length
    },
  )
</script>
